import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Pagination from "../../../components/Pagination";
import styles from "./index.module.css";
import SearchBar from "../../../components/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import FilterDropdown from "../../../components/FilterDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { getAllStaffMembers } from "../../../redux/features/staff/staffService";
import { getAllCourses } from "../../../redux/features/courses/coursesService";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";
import { getStudentPurchasenRequests } from "../../../redux/features/purchaseRequest/purchaseRequestService";
import { useNavigate } from "react-router-dom";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const Status = [
    { id: 'Approved', name: 'Approved' },
    { id: 'Reject', name: 'Reject' },
    { id: 'Pending', name: 'Pending' },
];

const PurchaseRequest = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const courseDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchValue, setSearchValue] = useState(null);
    const [selectedCoordinator, setSelectedCoordinator] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const { isCoursesLoading, courses } = useSelector((state) => state.course.allCourses);

    const { data, pageCount } = useSelector(
        (state) => state.purchaseRequest.purchaseRequests
    );
    const { isStaffMembersLoading, staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const totalPages = pageCount || 1;

    const fetchRegistrationRequests = useCallback(
        (filters = {}) => {
            const params = {
                search: searchTerm,
                course_id: selectedCourse && selectedCourse,
                status: selectedStatus && selectedStatus,
                skip: currentPage - 1,
                limit: 10,
                ...filters,
            };
            dispatch(getStudentPurchasenRequests(params));
        },
        [dispatch, searchTerm, selectedCourse, selectedStatus, currentPage]
    );

    useEffect(() => {
        fetchRegistrationRequests();
    }, [fetchRegistrationRequests]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setCurrentPage(1);
        fetchRegistrationRequests({ search: searchTerm });
    };

    const handleSelect = (option) => {
        setSelectedCourse(option.id);
        setCurrentPage(1);
        fetchRegistrationRequests({ course_or_program_id: option.id });
    };

    const handleStatus = (option) => {
        setSelectedStatus(option.id);
        setCurrentPage(1);
        fetchRegistrationRequests({ status: option.id });
    };

    const handleClearFilters = () => {
        setCurrentPage(1);
        setSelectedCourse(null);
        setSelectedStatus(null);
        setSearchTerm("");
        setSearchValue("");
        if (courseDropdownRef.current) {
            courseDropdownRef.current.clearSelection();
        }
        if (statusDropdownRef.current) {
            statusDropdownRef.current.clearSelection();
        }
        fetchRegistrationRequests({
            search: null,
            course_or_program_id: null,
            coordinator_id: null,
            status: null,
        });
    };

    useEffect(() => {
        dispatch(getAllCourses({ status: "true" }));
        dispatch(getAllStaffMembers({ role_name: "coordinator" }));
    }, [dispatch]);

    const formattedCourses = useMemo(() => {
        return courses.map(course => ({
            id: course.id,
            name: course.title
        }));
    }, [courses]);

    const openRequest = (id) => {
        navigate(`/admin/purchase-requests/${id}`)
    }
    return (
        <div className={`search-card`}>
            <div>
                <div className={`search-card-header  ${styles.searchHeader}`}>
                    <SearchBar
                        value={searchValue}
                        onSearch={handleSearch}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <div className={styles.headerRight}>
                        <div className={styles.headerDropdownSec}>
                            <FilterDropdown
                                ref={courseDropdownRef}
                                options={formattedCourses}
                                placeholder="Sort by Course/Program"
                                onSelect={handleSelect}
                            />
                            <FilterDropdown
                                ref={statusDropdownRef}
                                options={Status}
                                placeholder="Sort by Status"
                                onSelect={handleStatus}
                            />
                        </div>
                        <div className={styles.clearButton} onClick={handleClearFilters}>
                            <FontAwesomeIcon icon={faCircleXmark} /> <span>Clear All</span>
                        </div>
                    </div>
                </div>
                <div className={`search-card-body`}>
                    {isStaffMembersLoading ?
                        <TableSkeleton columns={["Full Name", "Email", "Course", "Price", "Discount Price", "Status", "Actions"]} rowCount={10} height={20} />
                        : data?.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Full Name</th>
                                            <th>Email</th>
                                            <th>Course</th>
                                            <th>Price</th>
                                            <th>Discount Price</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {data?.map((request) => (
                                            <tr key={request.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {request?.order?.user?.full_name}
                                                </td>
                                                <td>{request?.order?.user?.email}</td>
                                                <td>{request?.course?.title}</td>
                                                <td>{formatNumber(request?.course?.price)}</td>
                                                <td>{formatNumber(request?.course?.discount_price)}</td>
                                                <td><span className={`status-text-label ${request?.status?.toLowerCase()}`}>{request?.status}</span></td>
                                                <td className={`actionsBtn`}><button className={`view-btn`} onClick={() => openRequest(request.id)}>View</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
            </div>
            <div className={`search-card-footer`}>
                {data?.length > 0 && (
                    <div className={styles.paginationDiv}>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseRequest;